export interface BCProductVariant {
  variantId?: string | null;
  stacklineSku?: string | null;
}

export interface BCProductMetaData {
  stacklineSku?: string;
  currentVariantId?: string | number | null;
  source?: string;
  /** Pages storefront: false hides proactive concierge UI for this page. Omitted = no page veto. */
  showConcierge?: boolean;
  /** Pages storefront: false hides the Brandclub cart and add-to-bag chrome. Omitted = no page veto. */
  showCheckout?: boolean;
  variations:
    | {
        [variantId: string]: BCProductVariant;
      }
    | undefined;
}

declare global {
  interface Window {
    bcCatalogVersion?: string;
    bcStoreKind?: string;
    bcProductMetaData?: BCProductMetaData;
  }
}

const CATALOG_V2_VERSION = 'v2';

// Window access
const getBCProductMetaData = (): BCProductMetaData | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.bcProductMetaData;
};

// Catalog context
export const isCatalogV2ProductContext = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.bcCatalogVersion?.toLowerCase() === CATALOG_V2_VERSION;
};

export const getCurrentStoreKind = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.bcStoreKind;
};

// Stackline SKU resolution
const getVariationStacklineSku = (bcData: BCProductMetaData, variantId?: string | null): string | undefined => {
  return variantId ? bcData.variations?.[variantId]?.stacklineSku ?? undefined : undefined;
};

const normalizeVariantId = (variantId: string | number | undefined | null): string | undefined => {
  if (variantId == null || String(variantId) === '') {
    return undefined;
  }
  return String(variantId);
};

export const getStacklineSkuFromVariantId = (variantId: string | undefined | null): string | undefined => {
  const bcData = getBCProductMetaData();
  if (!bcData) {
    return undefined;
  }

  return getVariationStacklineSku(bcData, normalizeVariantId(variantId));
};

export const getCurrentCatalogV1StacklineSku = (selectedVariantId?: string | number | null): string | undefined => {
  const bcData = getBCProductMetaData();
  if (!bcData) {
    return undefined;
  }

  const selectedVariantIdString = normalizeVariantId(selectedVariantId);
  if (selectedVariantIdString) {
    return getVariationStacklineSku(bcData, selectedVariantIdString);
  }

  return bcData.stacklineSku;
};

/**
 * Retrieves unique Stackline SKUs from window.bcProductMetaData, including the main product and its variations.
 *
 * @returns {string[]} Array of unique SKUs.
 */
export const getStacklineSkus = (): string[] => {
  const bcData = getBCProductMetaData();
  if (!bcData) {
    return [];
  }

  const stacklineSkusSet = new Set<string>();
  if (bcData.stacklineSku) {
    stacklineSkusSet.add(bcData.stacklineSku);
  }
  Object.values(bcData.variations ?? {}).forEach((variation) => {
    if (variation.stacklineSku) {
      stacklineSkusSet.add(variation.stacklineSku);
    }
  });
  return Array.from(stacklineSkusSet);
};

export const resolveStacklineSkuFromUrlParams = (
  searchURL: string,
): {
  dtcVariantId?: string;
  stacklineSku: string | undefined;
} => {
  const queryParams = new URLSearchParams(searchURL);
  const variantId = queryParams.get('variant');
  if (variantId) {
    return {
      dtcVariantId: variantId,
      stacklineSku: getBCProductMetaData()?.variations?.[variantId]?.stacklineSku ?? undefined,
    };
  }
  return { stacklineSku: getBCProductMetaData()?.stacklineSku };
};

// Retailer SKU resolution
export const getRetailerSkuFromVariantId = (variantId: string | number | undefined | null): string | undefined => {
  if (variantId == null) {
    return undefined;
  }

  const variantIdString = String(variantId);
  if (!variantIdString) {
    return undefined;
  }
  const bcData = getBCProductMetaData();
  const retailerSku = bcData?.variations?.[variantIdString]?.variantId ?? variantIdString;
  return String(retailerSku);
};

export const getCurrentRetailerSku = (selectedVariantId?: string | number | null): string | undefined => {
  const bcData = getBCProductMetaData();
  const currentVariantId =
    selectedVariantId != null && String(selectedVariantId) !== '' ? selectedVariantId : bcData?.currentVariantId;
  return getRetailerSkuFromVariantId(currentVariantId);
};

export const getCurrentCatalogV2RetailerSku = (selectedVariantId?: string | number | null): string | undefined => {
  const bcData = getBCProductMetaData();
  const currentVariantId = normalizeVariantId(selectedVariantId) ?? normalizeVariantId(bcData?.currentVariantId);
  return getRetailerSkuFromVariantId(currentVariantId);
};

// Source resolution
export const getCurrentProductContextSource = (): 'pages' | undefined => {
  return getBCProductMetaData()?.source === 'pages' ? 'pages' : undefined;
};

/** Pages-only proactive concierge veto from the live page store (false = hide bubble). */
export const getPagesShowConciergeFromMeta = (): boolean | undefined => {
  const meta = getBCProductMetaData();
  if (meta?.source !== 'pages') return undefined;
  return meta.showConcierge;
};

/** Pages-only cart veto from composed checkout gates (false = hide bag drawer and bar). */
export const getPagesShowCheckoutFromMeta = (): boolean | undefined => {
  const meta = getBCProductMetaData();
  if (meta?.source !== 'pages') return undefined;
  return meta.showCheckout;
};

export default getStacklineSkus;
